import { useEffect, useState, useContext } from 'react';

import Cupcake from 'components/icons/illustrative/Cupcake';
import Egg from 'components/icons/illustrative/Egg';
import Salad from 'components/icons/illustrative/Salad';
import Smoothie from 'components/icons/illustrative/Smoothie';
import Taco from 'components/icons/illustrative/Taco';
import { BrandContext } from 'context/BrandContext';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import {
  BOX_MEALS_SIGNUP_ANSWERS,
  DINNERS,
  LUNCHES,
  DESSERTS,
  BREAKFASTS,
  SNACKS_AND_BEVERAGES,
  BOX_RECOMMENDER_PATHS,
  TEMPO,
} from 'lib/constants';
import { ICON_SIZES } from 'lib/constants/iconSizes';
import * as ErrorLogging from 'services/ErrorLogging';
import RequestService from 'services/RequestService';

const useBoxRecommender = ({ boxRecommenderPath, mealPlanSlug, onNavigate, location }) => {
  const [boxSignupData, setBoxSignupData] = useState({ question: '', answers: [], pathname: '' });
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { brand } = useContext(BrandContext);

  const showMealTimes =
    mealPlanSlug !== TEMPO && boxRecommenderPath === BOX_RECOMMENDER_PATHS.BOX_MEALS;

  const TacoIcon = () => <Taco svgSize={ICON_SIZES.MD} className="" />;
  const SaladIcon = () => <Salad svgSize={ICON_SIZES.MD} className="" />;
  const CupcakeIcon = () => <Cupcake svgSize={ICON_SIZES.MD} className="" />;
  const EggIcon = () => <Egg svgSize={ICON_SIZES.MD} className="" />;
  const SmoothieIcon = () => <Smoothie svgSize={ICON_SIZES.MD} className="" />;

  const iconMap = {
    [DINNERS]: TacoIcon,
    [LUNCHES]: SaladIcon,
    [DESSERTS]: CupcakeIcon,
    [BREAKFASTS]: EggIcon,
    [SNACKS_AND_BEVERAGES]: SmoothieIcon,
  };

  useEffect(() => {
    const fetchAnswers = async () => {
      try {
        const response = await RequestService.getBoxSignupAnswers({
          params: { boxRecommendation: { pathname: boxRecommenderPath } },
        });
        if (response.ok) {
          setBoxSignupData({
            question: response.question,
            answers: response.boxSignupAnswers,
            pathname: response.pathname,
          });
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
    };

    fetchAnswers();
  }, [boxRecommenderPath]);

  useEffect(() => {
    if (!error || showMealTimes) return;
    if (boxSignupData.answers.some(answer => answer.selected)) {
      setError('');
    }
  }, [boxSignupData.answers, error, showMealTimes]);

  const handleBoxSignupAnswerClick = answer => {
    const newAnswers = boxSignupData.answers.map(a => {
      if (a.id === answer.id) {
        return { ...a, selected: !a.selected };
      } else if (BOX_MEALS_SIGNUP_ANSWERS.includes(a.name)) {
        return a;
      } else {
        return { ...a, selected: false };
      }
    });

    setBoxSignupData({ ...boxSignupData, answers: newAnswers });
  };
  const handleContinueClick = useAnalyticsCallback(
    async () => {
      if (
        showMealTimes &&
        boxSignupData.answers
          .filter(answer => [DINNERS, LUNCHES].includes(answer.name))
          .every(answer => !answer.selected)
      ) {
        setError('Lunch or dinner is required to build your box');
        return;
      } else if (boxSignupData.answers?.every(answer => !answer.selected)) {
        setError('Please select at least one option');
        return;
      }

      setIsSubmitting(true);
      const answerIds = boxSignupData.answers.filter(a => a.selected).map(a => a.id);
      try {
        const response = await RequestService.updateBoxSignupAnswerSelections({
          params: { boxRecommendation: { answerIds, pathname: boxSignupData.pathname } },
        });

        if (response.ok) {
          if (onNavigate) onNavigate();
          return true;
        } else {
          setError(response.error);
          return false;
        }
      } catch (error) {
        setError(error);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    ({ events, logEvent }) => {
      const pageSubmitted = location ? location.amplitudePageSubmitted : '';
      const selectedAnswers = boxSignupData.answers
        .filter(a => a.selected)
        .map(a => a.name)
        .join(', ');

      const eventToLog = events.signUp?.[brand]?.boxRecommender?.[pageSubmitted] || pageSubmitted;

      logEvent(eventToLog, {
        mealPlanSlug: mealPlanSlug,
        softballQuestionsInputs: selectedAnswers,
      });
    },
    [brand, boxSignupData, mealPlanSlug, showMealTimes, boxRecommenderPath]
  );

  return {
    boxSignupData,
    error,
    setError,
    isSubmitting,
    setIsSubmitting,
    handleBoxSignupAnswerClick,
    setBoxSignupData,
    handleContinueClick,
    icons: { iconMap },
  };
};
export default useBoxRecommender;
